<script setup lang="ts">
import CBaseHeader from '@/presentation/components/base/CBaseHeader.vue'
import { useEMethodosStore } from '@/presentation/stores/emethodos/emethodos.ts'
import { computed, onMounted, ref, type Ref } from 'vue'
import { storeToRefs } from 'pinia'
import { default as PMessage } from 'primevue/message'
import { default as PProgressSpinner } from 'primevue/progressspinner'

// Composables
const eMethodosStore = useEMethodosStore()

// Store refs
const { eMethodosStoreLoading, eMethodosStoreError } = storeToRefs(eMethodosStore)

// Refs
const componentLoading: Ref<boolean> = ref(true)
const localError: Ref<string | null> = ref(null)
const eMethodosLoaded: Ref<boolean> = ref(false)

// Computed
const loading = computed(() => {
  return (componentLoading.value || eMethodosStoreLoading.value.fetchAllEMethodos) ?? false
})

const hasError = computed(() => {
  return (localError.value !== null || eMethodosStoreError.value.hasError) ?? false
})

const errorMessage = computed(() => {
  return (localError.value || eMethodosStoreError.value.message) ?? ''
})

/**
 * Charge les données des e-methodos
 */
const loadData = async (forceRefresh = false) => {
  try {
    componentLoading.value = true
    localError.value = null

    // Charger les écoles si nécessaire
    if (!eMethodosStoreLoading.value.fetchAllEMethodos || forceRefresh) {
      await loadEMethodos()
    }
  } catch (err) {
    console.error('Erreur lors du chargement des données:', err)
    localError.value =
      err instanceof Error ? err.message : 'Une erreur est survenue lors du chargement des données'
  } finally {
    componentLoading.value = false
  }
}

/**
 * Charge la des e-methodos
 */
const loadEMethodos = async () => {
  try {
    // Vérifier si les e-methodos doivent être rechargées
    if (eMethodosStore.eMethodosCount === 0 || !eMethodosStore.eMethodosStoreLastUpdated) {
      await eMethodosStore.fetchAllEMethodos()
    }
    eMethodosLoaded.value = true
  } catch (err) {
    console.error('Erreur lors du chargement des e-methodos:', err)
    throw err
  }
}

// LIFECYCLE
onMounted(async () => {
  try {
    await loadData(false)
  } catch (err) {
    console.error('Erreur lors du chargement initial:', err)
  }
})
</script>

<template>
  <c-base-header title="E-Methodos" :display-breadcrumbs="false" :has-widget="false" />
  <div class="col-span-12 flex-1 gap-8 overflow-y-auto p-8 pt-2">
    <!-- Affichage des erreurs -->
    <p-message v-if="hasError" severity="error" :closable="false" class="mb-4">
      {{ errorMessage }}
    </p-message>

    <!-- État de chargement principal -->
    <div v-if="loading && !hasError" class="flex flex-col items-center justify-center p-8">
      <p-progress-spinner style="width: 50px; height: 50px" strokeWidth="5" />
      <p class="mt-4 text-lg">Chargement des données...</p>
    </div>
    <!-- Contenu de la page -->
    <router-view v-else />
  </div>
</template>
